import React from 'react';
import {
  mParticleTrackSessionAttributes,
  useDevice,
  getUtmParameters,
  storage,
} from '@treatwell/ui';
import { ChannelOutput } from 'js/model/rainbow/content/ChannelOutput';
import { getPageDataTrackingProps } from 'js/mparticle/getPageDataTrackingProps';
import { trackPageViewEvent } from 'js/mparticle/mParticleTracking';
import { Context } from '../components/LocaleWrapper';

const getNavigationEntry = (): PerformanceNavigationTiming | void => {
  const navigationEntry =
    window.performance &&
    performance.getEntriesByType &&
    (performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming);

  // Check to ensure the `responseStart` property is present and valid.
  // In some cases no value is reported by the browser (for
  // privacy/security reasons), and in other cases (bugs) the value is
  // negative or is larger than the current page time.
  if (
    navigationEntry &&
    navigationEntry.responseStart > 0 &&
    navigationEntry.responseStart < performance.now()
  ) {
    return navigationEntry;
  }
};

type PageLoadMetrics = {
  ['full_page_load_time']: string;
  ['server_response_time']: string;
  ['dom_content_load_time']: string;
  ['ttfb_load_time']: string;
  ['dom_complete_load_time']: string;
};

const getReportPageLoadMetrics = (): PageLoadMetrics | void => {
  const navigationEntry = getNavigationEntry();

  if (!navigationEntry) {
    return;
  }

  return {
    full_page_load_time: (
      navigationEntry.loadEventEnd - navigationEntry.startTime
    ).toFixed(2),
    server_response_time: (
      navigationEntry.responseEnd - navigationEntry.fetchStart
    ).toFixed(2),
    dom_content_load_time: (
      navigationEntry.domContentLoadedEventEnd - navigationEntry.startTime
    ).toFixed(2),
    ttfb_load_time: (
      navigationEntry.responseStart - navigationEntry.startTime
    ).toFixed(2),
    dom_complete_load_time: (
      navigationEntry.domComplete - navigationEntry.startTime
    ).toFixed(2),
  };
};

const getPageViewAttributes = (
  countryCode: ChannelOutput['country']['countryCode'],
  { href, pathname, search }: Location,
  { referrer }: Document
) => ({
  country: countryCode,
  platform: 'web',
  url: href,
  url_path: pathname,
  query_parameters: search,
  referrer,
});

export function useTrackPageAndSessionView() {
  const { channel } = React.useContext(Context);
  const countryCode = channel.country.countryCode;

  React.useEffect(() => {
    const pageViewAttributes = getPageViewAttributes(
      countryCode,
      window.location,
      document
    );
    const utmParameters = getUtmParameters(window.location.search);

    trackPageViewEvent('page_view', {
      ...pageViewAttributes,
      ...utmParameters,
    });

    mParticleTrackSessionAttributes(countryCode);
  }, [countryCode]);
}

export const TrackBrowsePageAndSessionView = ({
  hasPopularTreatments,
  children,
  treatmentType,
  treatment,
}: {
  hasPopularTreatments: boolean;
  children: React.ReactNode;
  treatmentType?: string;
  treatment?: string;
}) => {
  const { channel } = React.useContext(Context);
  const { isMobile } = useDevice();
  const countryCode = channel.country.countryCode;

  React.useEffect(() => {
    const utmParameters = getUtmParameters(window.location.search);
    const pageViewAttributes = getPageViewAttributes(
      countryCode,
      window.location,
      document
    );

    trackPageViewEvent('browse', {
      section: 'exploration',
      is_web_mobile: isMobile,
      treatment_type: treatmentType,
      popular_treatments_displayed: hasPopularTreatments,
      treatment,
      ...pageViewAttributes,
      ...utmParameters,
    });

    mParticleTrackSessionAttributes(countryCode);
  }, [countryCode, hasPopularTreatments, isMobile, treatment, treatmentType]);

  return children;
};

export const TrackVenuePageAndSessionView = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { channel, pageData } = React.useContext(Context);
  const countryCode = channel.country.countryCode;
  const venueId = pageData.venue.venue.id.toString();
  const pageDataTrackingProps = getPageDataTrackingProps(pageData.venue.venue);

  React.useEffect(() => {
    const utmParameters = getUtmParameters(window.location.search);
    const pageViewAttributes = getPageViewAttributes(
      countryCode,
      window.location,
      document
    );
    const loadSpeedMetrics = getReportPageLoadMetrics();
    const isPinnedVenue =
      storage.session.getItem('pinned_venue')?.toString() === venueId;

    trackPageViewEvent('venue_menu', {
      section: 'booking_flow',
      venue_id: venueId,
      is_search_pinned: isPinnedVenue || undefined,
      ...pageViewAttributes,
      ...utmParameters,
      ...loadSpeedMetrics,
      ...pageDataTrackingProps,
    });

    mParticleTrackSessionAttributes(countryCode);
  }, [countryCode, venueId]);

  return children;
};

export const useTrackReviewConfirmationPageAndSessionView = ({
  treatment,
  treatmentType,
  socialShareDisplayed,
}: {
  treatment?: string;
  treatmentType?: string;
  socialShareDisplayed?: boolean;
} = {}) => {
  const { channel } = React.useContext(Context);
  const { isMobile } = useDevice();
  const countryCode = channel.country.countryCode;

  React.useEffect(() => {
    const utmParameters = getUtmParameters(window.location.search);
    const pageViewAttributes = getPageViewAttributes(
      countryCode,
      window.location,
      document
    );

    trackPageViewEvent('review_confirmation', {
      section: 'review_flow',
      is_web_mobile: isMobile,
      treatment_type: treatmentType,
      treatment,
      social_share_displayed: socialShareDisplayed,
      ...pageViewAttributes,
      ...utmParameters,
    });

    mParticleTrackSessionAttributes(countryCode);
  }, [treatment, treatmentType, socialShareDisplayed, countryCode, isMobile]);
};
